import {ThreadArtifactResult} from '../types.ts';

/**
 * Collects generated-file artifacts from a message's metadata into a single map keyed by
 * the producer's own key (fileId / sandbox path). Three producers write artifacts:
 *
 * - OpenAI code interpreter → `code_interpreter_files` (JSON, snake_case ThreadArtifactResult)
 * - OpenAI hosted shell     → `shell_files`            (JSON, snake_case ThreadArtifactResult)
 * - Azure dynamic sessions  → `tool_call_artifacts`    (JSON object, camelCase, keyed by
 *                                                        /mnt/data path, purpose
 *                                                        "dynamic_session_output")
 *
 * The dynamic-session form is normalized to the snake_case `ThreadArtifactResult` shape the
 * renderers expect. The pipe-delimited `tool_call_artifacts` form (used by other tools for
 * search results) is left untouched — it is handled separately.
 */
const DYNAMIC_SESSION_PURPOSE = 'dynamic_session_output';

const readString = (
    source: Record<string, unknown>,
    ...keys: string[]
): string | undefined => {
    for (const key of keys) {
        const value = source[key];
        if (typeof value === 'string' && value.length > 0) {
            return value;
        }
    }
    return undefined;
};

const readBool = (
    source: Record<string, unknown>,
    ...keys: string[]
): boolean | undefined => {
    for (const key of keys) {
        const value = source[key];
        if (typeof value === 'boolean') {
            return value;
        }
    }
    return undefined;
};

// Accepts either camelCase (dynamic sessions) or snake_case and returns the snake_case
// ThreadArtifactResult shape the UI components read. `purpose` is not part of that shape,
// so it is read separately by the caller for filtering.
const normalizeArtifact = (
    source: Record<string, unknown>
): ThreadArtifactResult => ({
    thread_artifact_id: readString(
        source,
        'threadArtifactId',
        'thread_artifact_id'
    ),
    artifact_url: readString(source, 'artifactUrl', 'artifact_url'),
    file_name: readString(source, 'fileName', 'file_name'),
    file_download_markdown: readString(
        source,
        'fileDownloadMarkdown',
        'file_download_markdown'
    ),
    is_error: readBool(source, 'isError', 'is_error'),
    error_message: readString(source, 'errorMessage', 'error_message'),
    path: readString(source, 'path')
});

const getInterpreterArtifactsFromMetadata = (
    metadata?: Record<string, string>
): Record<string, ThreadArtifactResult> => {
    const files: Record<string, ThreadArtifactResult> = {};

    // OpenAI code-interpreter / shell: already snake_case, assign as-is.
    for (const key of ['code_interpreter_files', 'shell_files']) {
        const value = metadata?.[key];
        if (!value) {
            continue;
        }
        try {
            Object.assign(
                files,
                JSON.parse(value) as Record<string, ThreadArtifactResult>
            );
        } catch (e) {
            console.error(`Failed to parse ${key} metadata:`, e);
        }
    }

    // Dynamic sessions: JSON object keyed by container path (camelCase). The same key is
    // also used in a pipe-delimited form by other tools, so only parse the JSON form.
    const toolCallArtifacts = metadata?.tool_call_artifacts;
    if (toolCallArtifacts && toolCallArtifacts.trimStart().startsWith('{')) {
        try {
            const parsed = JSON.parse(toolCallArtifacts) as Record<
                string,
                unknown
            >;
            for (const [key, value] of Object.entries(parsed)) {
                if (!value || typeof value !== 'object') {
                    continue;
                }
                const source = value as Record<string, unknown>;
                if (readString(source, 'purpose') !== DYNAMIC_SESSION_PURPOSE) {
                    continue;
                }
                files[key] = normalizeArtifact(source);
            }
        } catch (e) {
            console.error('Failed to parse tool_call_artifacts metadata:', e);
        }
    }

    return files;
};

export default getInterpreterArtifactsFromMetadata;
